import { useState, useEffect } from "react";
import { DashboardService } from "src/services";
import { defaultDateRange } from "src/helpers";
import {
  ActiveAntsPurchaseOrderColumnHeader,
  CheckInsGroupsOptions,
  CreationDateSortOptions,
  PurchaseOrderColumnHeader,
  TimestampSortOptions,
  TransferOrderColumnHeader,
  TransitPurchaseOrderColumnHeader,
  UserColumnHeader,
  VendorColumnHeader,
  WarehouseCheckInColumnHeader,
  ClaimColumnHeader
} from "src/enums";
import {
  ActiveAntsPurchaseOrdersFilters,
  DateTimeRange,
  PurchaseOrdersFilters,
  TransferOrdersFilters,
  TransitPurchaseOrdersFilters,
  UsersFiltersState,
  VendorsFiltersState,
  WarehouseCheckInsFilters,
  ClaimsFilters
} from "src/types";

export const useDashSettings = <
  Z extends
    | PurchaseOrderColumnHeader
    | TransferOrderColumnHeader
    | ActiveAntsPurchaseOrderColumnHeader
    | TransitPurchaseOrderColumnHeader
    | WarehouseCheckInColumnHeader
    | UserColumnHeader
    | VendorColumnHeader
    | ClaimColumnHeader,
  T extends
    | PurchaseOrdersFilters
    | TransferOrdersFilters
    | ActiveAntsPurchaseOrdersFilters
    | TransitPurchaseOrdersFilters
    | WarehouseCheckInsFilters
    | UsersFiltersState
    | VendorsFiltersState
    | ClaimsFilters,
  S extends CreationDateSortOptions | TimestampSortOptions,
  G extends CheckInsGroupsOptions | undefined
>(
  dashService: DashboardService<Z, T, S, G>,
  defaultSort: () => S,
  onDashSettingsChange?: () => void,
  defaultDateRangeFn: () => DateTimeRange = defaultDateRange
) => {
  const currentDashSettings = dashService.getLocalStorageDashSettings();

  const [filter, setFilter] = useState<T | undefined>(currentDashSettings?.filter);
  const [sort, setSort] = useState<S>(currentDashSettings?.sort ?? defaultSort());
  const [searchTerm, setSearchTerm] = useState<string>();
  const [dateTimeRange, setDateTimeRange] = useState<DateTimeRange>(
    currentDashSettings?.dateTimeRange ?? defaultDateRangeFn()
  );
  const [deliveryDateTimeRange, setDeliveryDateTimeRange] = useState<DateTimeRange>(
    currentDashSettings?.deliveryDateTimeRange ?? { start: undefined, end: undefined }
  );
  const [receiptDateTimeRange, setReceiptDateTimeRange] = useState<DateTimeRange>(
    currentDashSettings?.receiptDateTimeRange ?? { start: undefined, end: undefined }
  );
  const [columnsToHide, setColumnsToHide] = useState<Z[]>(currentDashSettings?.columnsToHide ?? []);
  const [group, setGroup] = useState<G | undefined>(currentDashSettings?.group);

  useEffect(() => {
    onDashSettingsChange?.();
    dashService.setLocalStorageDashSettings({
      filter,
      sort,
      dateTimeRange,
      deliveryDateTimeRange,
      receiptDateTimeRange,
      columnsToHide,
      group
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashService, filter, sort, dateTimeRange, deliveryDateTimeRange, receiptDateTimeRange, columnsToHide, group]);

  return {
    filter,
    setFilter,
    sort,
    setSort,
    searchTerm,
    setSearchTerm,
    dateTimeRange,
    setDateTimeRange,
    deliveryDateTimeRange,
    setDeliveryDateTimeRange,
    receiptDateTimeRange,
    setReceiptDateTimeRange,
    columnsToHide,
    setColumnsToHide,
    group,
    setGroup
  };
};
